const primary = [
  {
    icon: "ri-tools-line",
    title: "Solar Repair & Maintenance",
    benefit: "Restore lost production",
    detail: "Underperforming system? We diagnose the root cause — inverter faults, shading issues, degraded panels, wiring failures — and fix it right. Protect your investment and recover lost output.",
    tag: "Core Service",
    image: "https://public.readdy.ai/ai/img_res/aba60aaed18c24c0c3231e476d6c32a6.jpg",
  },
  {
    icon: "ri-battery-charge-line",
    title: "Battery Storage Solutions",
    benefit: "Never lose power again",
    detail: "Add battery backup to your existing solar system or as a standalone solution. Store energy for outages, peak rate hours, and true grid independence. We size and install the right system for your needs.",
    tag: "Energy Independence",
    image: "https://readdy.ai/api/search-image?query=modern%20home%20battery%20storage%20system%20Tesla%20Powerwall%20mounted%20cleanly%20on%20garage%20wall%2C%20professional%20installation%2C%20warm%20interior%20lighting%2C%20premium%20residential%20setting%2C%20product%20photography%20quality&width=800&height=500&seq=svc-battery-v3&orientation=landscape",
  },
];

const secondary = [
  {
    icon: "ri-flashlight-line",
    title: "Electrical Services",
    benefit: "Panel upgrades & full electrical",
    detail: "Breaker panel upgrades, troubleshooting, rewiring, and code compliance. Licensed electricians for residential and commercial.",
  },
  {
    icon: "ri-alarm-warning-line",
    title: "Emergency Electrical",
    benefit: "24/7 rapid response",
    detail: "Electrical failures don't wait. Our licensed team responds fast — day, night, or weekend.",
    tag: "24/7",
  },
  {
    icon: "ri-charging-pile-2-line",
    title: "EV Charger Installation",
    benefit: "Level 2 home & commercial",
    detail: "Full-scope EV charger installs — panel assessment, permitting, and clean installation.",
  },
  {
    icon: "ri-search-eye-line",
    title: "System Optimization",
    benefit: "Maximize what you have",
    detail: "Performance audits, monitoring setup, and efficiency tuning for existing solar systems.",
  },
];

export default function Services() {
  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="services" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="max-w-2xl mb-16">
          <span className="text-amber-500 text-xs font-bold tracking-[0.2em] uppercase">What We Do</span>
          <h2
            className="text-4xl md:text-5xl font-black text-gray-950 mt-3 mb-5 leading-tight tracking-tight"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            Repair. Optimize.<br />Protect. Power Up.
          </h2>
          <p className="text-gray-500 text-base leading-relaxed max-w-lg">
            From fixing underperforming solar systems to adding battery backup and upgrading your electrical panel — we handle the full scope of your home&apos;s energy needs.
          </p>
        </div>

        {/* Primary featured cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          {primary.map((svc) => (
            <div
              key={svc.title}
              className="group relative rounded-2xl overflow-hidden cursor-pointer"
              style={{ height: "360px" }}
            >
              <img
                src={svc.image}
                alt={svc.title}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

              <div className="absolute top-5 left-5">
                <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full tracking-wide uppercase">
                  {svc.tag}
                </span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-7">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${svc.icon} text-amber-400`}></i>
                  </div>
                  <span className="text-amber-400 text-xs font-semibold uppercase tracking-widest">{svc.benefit}</span>
                </div>
                <h3
                  className="text-2xl font-black text-white mb-2 leading-tight"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                >
                  {svc.title}
                </h3>
                <p className="text-white/65 text-sm leading-relaxed">{svc.detail}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Secondary 4-col cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {secondary.map((svc) => (
            <div
              key={svc.title}
              className="group bg-stone-50 hover:bg-amber-50 rounded-xl p-6 border border-transparent hover:border-amber-200 transition-all duration-300 cursor-pointer relative"
            >
              {svc.tag && (
                <span className="absolute top-4 right-4 bg-red-50 text-red-500 text-xs font-bold px-2 py-0.5 rounded-full">
                  {svc.tag}
                </span>
              )}
              <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl mb-4 border border-gray-100 group-hover:border-amber-200 group-hover:bg-amber-50 transition-all">
                <i className={`${svc.icon} text-amber-500 text-xl`}></i>
              </div>
              <h3
                className="text-sm font-black text-gray-900 mb-1"
                style={{ fontFamily: "'Syne', sans-serif" }}
              >
                {svc.title}
              </h3>
              <p className="text-amber-600 text-xs font-semibold mb-2">{svc.benefit}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{svc.detail}</p>
            </div>
          ))}
        </div>

        {/* Post-services CTA */}
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={() => scrollTo("#contact")}
            className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-4 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            Schedule a Free Consultation
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-check-line"></i>
            </div>
          </button>
          <a
            href="tel:9258723408"
            className="text-gray-500 hover:text-amber-600 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line"></i>
            </div>
            Or call (925) 872-3408
          </a>
        </div>
      </div>
    </section>
  );
}
